import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import SearchBar from '../components/SearchBar';
import Layout from '../components/Layout';
import Pagination from '../components/Common/Pagination';

import CustomerCreateModal from '../components/Customer/CustomerCreateModal';
import CreateQueryModal from '../components/Query/CreateQueryModal';
import CreateFollowUpModal from '../components/Followup/CreateFollowupModal';
import CustomerInfoModal from '../components/Customer/CustomerInfoModal';

import { Customer } from '../types/Customer';
import '../css/Pages.css';
import '../css/PackagesPage.css';
import { fetchCustomers } from '../api/Customer';
import * as FiIcons from 'react-icons/fi';
const FaEye = FiIcons.FiEye as unknown as React.FC;
const FaPlus = FiIcons.FiPlus as unknown as React.FC;
const FiClock = FiIcons.FiClock as unknown as React.FC;

const PAGE_SIZE = 50;

const CustomersPage = () => {
  const hasFetched = useRef(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomerCreateModal, setShowCustomerCreateModal] = useState(false);
  const [showCreateQueryModal, setShowCreateQueryModal] = useState(false);
  const [showCreateFollowUpModal, setShowCreateFollowUpModal] = useState(false);
  const [customerId, setCustomerId] = useState(0);
  const [queryId, setQueryId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [serverPaginated, setServerPaginated] = useState(false);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const lastLoadedPageRef = useRef<number | null>(null);
  // Track which single field is revealed at a time: `${field}:${idOrIndex}`
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const maskPhone = (phone?: string) => {
    const s = String(phone || '').replace(/\s+/g, '');
    if (!s) return '-';
    const keep = 2;
    const endKeep = 2;
    const len = s.length;
    if (len <= keep + endKeep) return '*'.repeat(Math.max(3, len));
    const maskedLen = Math.max(3, len - keep - endKeep);
    return `${s.slice(0, keep)}${'*'.repeat(maskedLen)}${s.slice(-endKeep)}`;
  };

  const maskEmail = (email?: string) => {
    const s = String(email || '').trim();
    if (!s) return '-';
    const keep = 2;
    const endKeep = 2;
    const len = s.length;
    if (len <= keep + endKeep) return '*'.repeat(Math.max(3, len));
    const maskedLen = Math.max(3, len - keep - endKeep);
    return `${s.slice(0, keep)}${'*'.repeat(maskedLen)}${s.slice(-endKeep)}`;
  };

  const load = useCallback(async (targetPage: number) => {
    try {
      setLoading(true);
      const data = await fetchCustomers({ page: targetPage, limit: PAGE_SIZE });
      lastLoadedPageRef.current = targetPage;

      if (Array.isArray(data)) {
        setCustomers(data);
        setServerPaginated(false);
        setServerTotalPages(Math.max(1, Math.ceil(data.length / PAGE_SIZE)));
        return;
      }

      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.customers)
          ? data.customers
          : Array.isArray(data?.items)
            ? data.items
            : [];
      const pagination = data?.pagination || {};
      const total = Number(data?.total ?? pagination.total ?? pagination.count ?? list.length);
      const totalPages = Number(
        data?.totalPages ?? pagination.totalPages ?? pagination.pages ?? Math.max(1, Math.ceil(total / PAGE_SIZE))
      );
      setCustomers(prev => {
        if (targetPage <= 1) return list;
        const existingIds = new Set(prev.map((c: Customer) => c.id).filter(Boolean));
        const deduped = list.filter((c: Customer) => !c.id || !existingIds.has(c.id));
        return [...prev, ...deduped];
      });
      setServerPaginated(true);
      setServerTotalPages(totalPages);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    load(1);
  }, [load]);

  useEffect(() => {
    if (!hasFetched.current || !serverPaginated) return;
    if (lastLoadedPageRef.current === page) return;
    load(page);
  }, [page, serverPaginated, load]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const closeAllModals = () => {
    setShowCustomerCreateModal(false);
    setShowCreateQueryModal(false);
    setShowCreateFollowUpModal(false);
  };

  const handleAddQuery = (customerId?: number) => {
    setCustomerId(customerId || 0);
    setShowCreateQueryModal(true);
  };

  const navigate = useNavigate();
  const handleViewQueries = (customerId?: number) => {
    navigate(`/queries?customerId=${customerId}`);
  };

  const filteredCustomers = useMemo(() => (
    customers.filter(c =>
      (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.phoneNumber || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.city || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
  ), [customers, searchTerm]);

  const totalPages = serverPaginated
    ? Math.max(1, serverTotalPages)
    : Math.max(1, Math.ceil(filteredCustomers.length / PAGE_SIZE));
  const pagedCustomers = useMemo(() => {
    if (serverPaginated) return filteredCustomers;
    return filteredCustomers.slice(0, page * PAGE_SIZE);
  }, [filteredCustomers, page, serverPaginated]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 200;
        if (nearBottom && !loading && page < totalPages) {
          setPage(prev => Math.min(totalPages, prev + 1));
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [loading, page, totalPages]);

  return (
    <Layout>
      <div className="crm-page packages-page">
        <div className="packages-header">
          <div>
            <h2>Customers</h2>
            <p className="page-hint">Manage CRM customer records, contact details, and follow-up entry points.</p>
          </div>
          <div className="packages-actions">
            <button className="btn" onClick={() => setShowCustomerCreateModal(true)} title="Create a new customer">
              <FaPlus /> Add Customer
            </button>
          </div>
        </div>

        <div className="packages-filters">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search by name, phone, or city"
          />
        </div>

        <div className="packages-table-wrapper">
          <table className="packages-table customers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>City</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedCustomers
                .map((c, idx) => (
                  <tr key={c.id ?? idx}>
                    <td>
                      <span
                        className="clickable-name"
                        onClick={() => setSelectedCustomer(c)}
                      >
                        {c.name}
                      </span>
                    </td>
                    <td>
                      {(() => {
                        const key = `email:${c.id ?? 'idx-'+idx}`;
                        const isShown = revealedKey === key;
                        return isShown ? (
                          <a href={`mailto:${c.email}`}>{c.email}</a>
                        ) : (
                          <span className="masked" onClick={() => setRevealedKey(key)} title="Click to reveal email">
                            {maskEmail(c.email)}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      {(() => {
                        const key = `phone:${c.id ?? 'idx-'+idx}`;
                        const isShown = revealedKey === key;
                        return isShown ? (
                          <a href={`tel:${c.phoneNumber}`}>{c.phoneNumber}</a>
                        ) : (
                          <span className="masked" onClick={() => setRevealedKey(key)} title="Click to reveal phone">
                            {maskPhone(c.phoneNumber)}
                          </span>
                        );
                      })()}
                    </td>
                    <td>{c.city || '-'}</td>
                    <td className="col-actions">
                      <div className="action-buttons with-labels">
                        <button className="btn-secondary" title="View Queries" onClick={() => handleViewQueries(c.id)}>
                          <FaEye /> <span className="btn-label">View Queries</span>
                        </button>
                        <button className="btn-secondary" title="Add Query" onClick={() => handleAddQuery(c.id)}>
                          <FaPlus /> <span className="btn-label">Add Query</span>
                        </button>
                        <button className="btn-secondary" title="View Follow-ups" onClick={() => navigate(`/followups?customerId=${c.id}`)}>
                          <FiClock /> <span className="btn-label">Follow-ups</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {filteredCustomers.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty-state">No customers found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {selectedCustomer && (
        <CustomerInfoModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {showCustomerCreateModal && (
        <CustomerCreateModal
          onClose={closeAllModals}
          onCustomerCreated={async (id) => {
            setCustomerId(id);
            setShowCustomerCreateModal(false);
            setPage(1);
            await load(1);
            setShowCreateQueryModal(true);
          }}
        />
      )}

      {showCreateQueryModal && (
        <CreateQueryModal
          customerId={customerId}
          onClose={closeAllModals}
          onQueryCreated={(id) => {
            setQueryId(id);
            setShowCreateQueryModal(false);
            setShowCreateFollowUpModal(true);
          }}
        />
      )}

      {showCreateFollowUpModal && (
        <CreateFollowUpModal
          queryId={queryId}
          required
          onClose={closeAllModals}
        />
      )}
    </Layout>
  );
};

export default CustomersPage;
